/**
 * Phone Hooks for AmalQ
 * React Query hooks for phone number management and SMS verification
 * Following enterprise-grade patterns from existing codebase
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { phoneApi } from '../services/phoneApi';
import { profileQueryKeys } from './profileQueryKeys';
import {
  AddPhoneRequest,
  VerifyPhoneRequest,
  ChangePhoneRequest,
  PasswordResetRequest,
  PasswordResetVerifyRequest,
  PasswordResetCompleteRequest,
} from '../services/phoneApi';

/**
 * Query Keys for Phone Operations
 */
export const phoneQueryKeys = {
  all: ['phone'] as const,
  status: () => [...phoneQueryKeys.all, 'status'] as const,
  verification: () => [...phoneQueryKeys.all, 'verification'] as const,
};

/**
 * Hook: Get phone status
 * Fetches current phone verification status and details
 */
export function usePhoneStatus() {
  return useQuery({
    queryKey: phoneQueryKeys.status(),
    queryFn: phoneApi.getPhoneStatus,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
    retryDelay: 1000,
  });
}

/**
 * Hook: Add phone number mutation
 * Handles phone number addition with optimistic updates
 */
export function useAddPhone() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: AddPhoneRequest) => phoneApi.addPhoneNumber(request),
    onSuccess: () => {
      // Invalidate phone status to refetch fresh data
      queryClient.invalidateQueries({ queryKey: phoneQueryKeys.status() });
      queryClient.invalidateQueries({ queryKey: profileQueryKeys.verificationStatus() });
    },
    onError: (error) => {
      console.error('Phone addition failed:', error);
    },
  });
}

/**
 * Hook: Verify phone number mutation
 * Handles phone verification with 6-digit code
 */
export function useVerifyPhone() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: VerifyPhoneRequest) => phoneApi.verifyPhoneNumber(request),
    onSuccess: () => {
      // Invalidate phone status to refetch fresh data
      queryClient.invalidateQueries({ queryKey: phoneQueryKeys.status() });
      queryClient.invalidateQueries({ queryKey: profileQueryKeys.verificationStatus() });
    },
    onError: (error) => {
      console.error('Phone verification failed:', error);
    },
  });
}

/**
 * Hook: Change phone number mutation
 * Handles phone number change with verification
 */
export function useChangePhone() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: ChangePhoneRequest) => phoneApi.changePhoneNumber(request),
    onSuccess: () => {
      // Invalidate phone status to refetch fresh data
      queryClient.invalidateQueries({ queryKey: phoneQueryKeys.status() });
      queryClient.invalidateQueries({ queryKey: profileQueryKeys.verificationStatus() });
    },
    onError: (error) => {
      console.error('Phone change failed:', error);
    },
  });
}

/**
 * Hook: Remove phone number mutation
 * Handles phone number removal
 */
export function useRemovePhone() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => phoneApi.removePhoneNumber(),
    onSuccess: () => {
      // Invalidate phone status to refetch fresh data
      queryClient.invalidateQueries({ queryKey: phoneQueryKeys.status() });
      queryClient.invalidateQueries({ queryKey: profileQueryKeys.verificationStatus() });
    },
    onError: (error) => {
      console.error('Phone removal failed:', error);
    },
  });
}

/**
 * Hook: Request password reset mutation (enhanced with phone support)
 * Handles password reset requests via email or phone
 */
export function useRequestPasswordReset() {
  return useMutation({
    mutationFn: (request: PasswordResetRequest) => phoneApi.requestPasswordReset(request),
    onError: (error) => {
      console.error('Password reset request failed:', error);
    },
  });
}

/**
 * Hook: Verify password reset code mutation
 * Handles reset code verification
 */
export function useVerifyPasswordResetCode() {
  return useMutation({
    mutationFn: (request: PasswordResetVerifyRequest) => phoneApi.verifyPasswordResetCode(request),
    onError: (error) => {
      console.error('Password reset code verification failed:', error);
    },
  });
}

/**
 * Hook: Complete password reset mutation
 * Handles password reset completion
 */
export function useCompletePasswordReset() {
  return useMutation({
    mutationFn: (request: PasswordResetCompleteRequest) => phoneApi.completePasswordReset(request),
    onError: (error) => {
      console.error('Password reset completion failed:', error);
    },
  });
}

/**
 * Combined phone management hook for easy access to all phone operations
 * Provides a unified interface for phone-related functionality
 */
export function usePhoneManagement(): {
  // Queries
  phoneStatus: ReturnType<typeof usePhoneStatus>;
  
  // Mutations
  addPhone: ReturnType<typeof useAddPhone>;
  verifyPhone: ReturnType<typeof useVerifyPhone>;
  changePhone: ReturnType<typeof useChangePhone>;
  removePhone: ReturnType<typeof useRemovePhone>;
  
  // Password reset mutations
  requestPasswordReset: ReturnType<typeof useRequestPasswordReset>;
  verifyPasswordResetCode: ReturnType<typeof useVerifyPasswordResetCode>;
  completePasswordReset: ReturnType<typeof useCompletePasswordReset>;
  
  // Loading states
  isLoading: boolean;
  isAddingPhone: boolean;
  isVerifyingPhone: boolean;
  isChangingPhone: boolean;
  isRemovingPhone: boolean;
  isRequestingPasswordReset: boolean;
  isVerifyingPasswordResetCode: boolean;
  isCompletingPasswordReset: boolean;
} {
  const phoneStatus = usePhoneStatus();
  
  const addPhone = useAddPhone();
  const verifyPhone = useVerifyPhone();
  const changePhone = useChangePhone();
  const removePhone = useRemovePhone();
  
  const requestPasswordReset = useRequestPasswordReset();
  const verifyPasswordResetCode = useVerifyPasswordResetCode();
  const completePasswordReset = useCompletePasswordReset();

  return {
    // Queries
    phoneStatus,
    
    // Mutations
    addPhone,
    verifyPhone,
    changePhone,
    removePhone,
    
    // Password reset mutations
    requestPasswordReset,
    verifyPasswordResetCode,
    completePasswordReset,
    
    // Loading states
    isLoading: phoneStatus.isLoading,
    isAddingPhone: addPhone.isPending,
    isVerifyingPhone: verifyPhone.isPending,
    isChangingPhone: changePhone.isPending,
    isRemovingPhone: removePhone.isPending,
    isRequestingPasswordReset: requestPasswordReset.isPending,
    isVerifyingPasswordResetCode: verifyPasswordResetCode.isPending,
    isCompletingPasswordReset: completePasswordReset.isPending,
  };
}

/**
 * Hook: Phone verification status
 * Convenience hook for checking if phone is verified
 */
export function usePhoneVerificationStatus() {
  const { phoneStatus } = usePhoneManagement();
  
  return {
    isVerified: phoneStatus.data?.verified || false,
    hasPhone: phoneStatus.data?.hasPhone || false,
    canRemove: phoneStatus.data?.canRemove || false,
    phoneNumber: phoneStatus.data?.phoneNumber,
    verifiedAt: phoneStatus.data?.verifiedAt,
    isLoading: phoneStatus.isLoading,
    error: phoneStatus.error,
  };
}

/**
 * Hook: Phone verification flow
 * Manages the complete phone verification process
 */
export function usePhoneVerificationFlow() {
  const { addPhone, verifyPhone } = usePhoneManagement();
  
  return {
    addPhone,
    verifyPhone,
    isAdding: addPhone.isPending,
    isVerifying: verifyPhone.isPending,
    isComplete: addPhone.isSuccess && verifyPhone.isSuccess,
    error: addPhone.error || verifyPhone.error,
  };
}

/**
 * Hook: Password reset flow (enhanced with phone support)
 * Manages the complete password reset process via email or phone
 */
export function usePasswordResetFlow() {
  const { 
    requestPasswordReset, 
    verifyPasswordResetCode, 
    completePasswordReset 
  } = usePhoneManagement();
  
  return {
    requestPasswordReset,
    verifyPasswordResetCode,
    completePasswordReset,
    isRequesting: requestPasswordReset.isPending,
    isVerifying: verifyPasswordResetCode.isPending,
    isCompleting: completePasswordReset.isPending,
    isComplete: completePasswordReset.isSuccess,
    error: requestPasswordReset.error || verifyPasswordResetCode.error || completePasswordReset.error,
  };
}
